import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Checkbox, Divider, Flex, Heading, HStack, IconButton, Input,
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  NumberInput, NumberInputField, Select, Spinner, Text, Textarea, VStack, useToast,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { CorrectionsAPI, KBAPI, ProductsAPI } from '../../../services/api';
import MarkableText from './MarkableText';
import { notifyCorrectionsChanged } from './correctionReview';
import {
  ISSUE_TYPES, MAX_TEXT, cleanIssue, emptyIssue, issueProblem, typeLabel, typesForRole,
} from './correctionTypes';

const ORDINAL_ZH = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
const issueTitle = (index, lang) => (lang === 'zh' ? `问题${ORDINAL_ZH[index] || index + 1}` : `Problem ${index + 1}`);

const productName = (p) => p.product_name || p.name || p.title || p.sku || String(p.id);

function ProductPicker({ value, onChange, lang }) {
  const zh = lang === 'zh';
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const search = async (event) => {
    event?.preventDefault?.();
    setLoading(true);
    try {
      const page = await ProductsAPI.list({ q: q.trim() || undefined, page_size: 20 });
      setResults(page.items || page.products || (Array.isArray(page) ? page : []));
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };
  const chosen = new Set(value.map((p) => String(p.id)));
  const toggle = (p) => {
    const id = String(p.id);
    onChange(chosen.has(id) ? value.filter((x) => String(x.id) !== id) : [...value, { id, name: productName(p) }]);
  };
  return (
    <VStack align="stretch" spacing={2}>
      <HStack as="form" onSubmit={search}>
        <Input size="sm" value={q} onChange={(e) => setQ(e.target.value)} placeholder={zh ? '搜索商品库：型号、名称、SKU' : 'Search catalog: model, name, SKU'} />
        <Button size="sm" type="submit" isLoading={loading}>{zh ? '搜索' : 'Search'}</Button>
      </HStack>
      {value.length > 0 && (
        <HStack wrap="wrap" spacing={1}>
          {value.map((p) => <Badge key={p.id} colorScheme="green" textTransform="none">{p.name}</Badge>)}
        </HStack>
      )}
      <Box maxH="180px" overflowY="auto">
        {results.map((p) => (
          <Checkbox key={p.id} isChecked={chosen.has(String(p.id))} onChange={() => toggle(p)} display="flex" py={1}>
            <Text fontSize="sm">{productName(p)}{p.sku ? ` · ${p.sku}` : ''}</Text>
          </Checkbox>
        ))}
      </Box>
    </VStack>
  );
}

function PolicyPicker({ value, onChange, lang }) {
  const zh = lang === 'zh';
  const [all, setAll] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    KBAPI.list().then((rows) => setAll(Array.isArray(rows) ? rows : [])).catch(() => setAll([]));
  }, []);
  const chosen = new Set(value.map((p) => String(p.id)));
  const shown = (all || []).filter((row) => !q.trim() || `${row.question} ${row.answer}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 30);
  const toggle = (row) => {
    const id = String(row.id);
    onChange(chosen.has(id) ? value.filter((x) => String(x.id) !== id) : [...value, { id, question: row.question }]);
  };
  return (
    <VStack align="stretch" spacing={2}>
      <Input size="sm" value={q} onChange={(e) => setQ(e.target.value)} placeholder={zh ? '搜索已上传的 QA：退货、到货、价格…' : 'Search QA: returns, delivery, price…'} />
      {all === null ? <Spinner size="sm" /> : (
        <Box maxH="160px" overflowY="auto">
          {shown.map((row) => (
            <Checkbox key={row.id} isChecked={chosen.has(String(row.id))} onChange={() => toggle(row)} display="flex" py={1}>
              <Text fontSize="sm" noOfLines={2}>{row.question}</Text>
            </Checkbox>
          ))}
        </Box>
      )}
    </VStack>
  );
}

function IssueForm({ issue, index, role, text, onChange, onRemove, lang }) {
  const zh = lang === 'zh';
  const spec = ISSUE_TYPES[issue.type];
  const set = (patch) => onChange({ ...issue, ...patch });
  const needsSpans = ['spans_with_right', 'spans'].includes(spec?.requires) || ['wrong_recommendation', 'repeated_answer'].includes(issue.type);
  return (
    <Box border="1px solid" borderColor="gray.200" rounded="md" p={4}>
      <Flex justify="space-between" align="center" mb={3}>
        <Heading size="sm">{issueTitle(index, lang)}</Heading>
        {onRemove && <IconButton size="xs" variant="ghost" icon={<FiTrash2 />} aria-label={zh ? '删除这个问题' : 'Remove problem'} onClick={onRemove} />}
      </Flex>
      <Select size="sm" value={issue.type} onChange={(e) => onChange(emptyIssue(e.target.value))} mb={2}>
        {typesForRole(role).map((type) => <option key={type} value={type}>{typeLabel(type, lang)}</option>)}
      </Select>
      {spec && <Text fontSize="sm" color="gray.600" mb={3}>{zh ? spec.hintZh : spec.hintEn}</Text>}
      <VStack align="stretch" spacing={3}>
        {needsSpans && (
          <MarkableText text={text} spans={issue.spans} lang={lang}
            withRight={['spans_with_right'].includes(spec?.requires)}
            onChange={(spans) => set({ spans })} />
        )}
        {spec?.requires === 'products' && <ProductPicker value={issue.products} onChange={(products) => set({ products })} lang={lang} />}
        {issue.type === 'policy' && <PolicyPicker value={issue.policies} onChange={(policies) => set({ policies })} lang={lang} />}
        {issue.type === 'repeated_answer' && (
          <HStack>
            <Text fontSize="sm">{zh ? '重复次数' : 'Times repeated'}</Text>
            <NumberInput size="sm" maxW="100px" min={0} max={100} value={issue.repeat_count ?? ''} onChange={(_, n) => set({ repeat_count: Number.isNaN(n) ? '' : n })}>
              <NumberInputField />
            </NumberInput>
          </HStack>
        )}
        {['policy', 'user_other', 'agent_other', 'missing_ticket', 'wrong_recommendation'].includes(issue.type) && (
          <Box>
            <Text fontSize="sm" mb={1}>{issue.type === 'policy' ? (zh ? '正确答案' : 'Right answer') : (zh ? '正确的做法 / 理解' : 'What should have happened')}</Text>
            <Textarea size="sm" rows={2} maxLength={MAX_TEXT} value={issue.expected || ''} onChange={(e) => set({ expected: e.target.value })} />
            <Text fontSize="xs" color="gray.500" textAlign="right">{(issue.expected || '').length}/{MAX_TEXT}</Text>
          </Box>
        )}
        {['user_other', 'agent_other'].includes(issue.type) && (
          <Box>
            <Text fontSize="sm" mb={1}>{zh ? '具体问题' : 'What went wrong'}</Text>
            <Textarea size="sm" rows={2} maxLength={MAX_TEXT} value={issue.note || ''} onChange={(e) => set({ note: e.target.value })} />
          </Box>
        )}
      </VStack>
    </Box>
  );
}

// One sentence, one or more problems, one save (checklist items 14-17, 19-27).
// existing: a saved correction to edit; otherwise a new one for this turn.
export default function CorrectionEditor({ isOpen, onClose, onSaved, recordRef, source = 'call', turn, existing, lang = 'zh' }) {
  const zh = lang === 'zh';
  const toast = useToast();
  const role = existing?.turn_role || turn?.role;
  const text = existing?.original_text || turn?.content || '';
  const initial = useMemo(() => (existing?.issues?.length
    ? existing.issues.map((i) => ({ ...emptyIssue(i.type), ...i }))
    : [emptyIssue(typesForRole(role)[0])]), [existing, role]);
  const [issues, setIssues] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (isOpen) setIssues(initial); }, [isOpen, initial]);

  const problems = issues.map((i) => issueProblem(i, lang));
  const save = async () => {
    if (problems.some(Boolean)) return;
    setSaving(true);
    try {
      const payload = issues.map(cleanIssue);
      const saved = existing
        ? await CorrectionsAPI.update(existing.id, payload)
        : await CorrectionsAPI.create({ source, record_ref: recordRef, turn_ref: turn.turn_id || String(turn.index), turn_role: role, original_text: text, issues: payload });
      const notes = [
        saved.status === 'pending_review' && (zh ? '已提交，主账号审核通过后 AI 才会使用。' : 'Submitted; the AI uses it after the account owner approves.'),
        saved.escalated && (zh ? '同类错误已出现 3 次，已反馈技术团队。' : 'Seen 3 times; sent to the technical team.'),
      ].filter(Boolean);
      toast({ status: 'success', title: zh ? '已保存纠错' : 'Correction saved', description: notes.join(' ') || undefined });
      notifyCorrectionsChanged();
      onSaved?.(saved);
      onClose();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast({ status: 'error', title: zh ? '保存失败' : 'Save failed', description: detail?.code || error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent bg="white" color="gray.900">
        <ModalHeader>
          {existing ? (zh ? '修改纠错' : 'Edit correction') : (zh ? '纠错' : 'Correct')}
          <Text fontSize="sm" fontWeight="normal" color="gray.600">
            {role === 'user' ? (zh ? '客户这句话' : 'Customer said') : (zh ? 'AI 的回答' : 'AI answered')}
          </Text>
        </ModalHeader>
        <ModalCloseButton _hover={{ bg: 'gray.100' }} />
        <ModalBody>
          <Box p={3} bg="blue.50" rounded="md" mb={4}><Text>{text}</Text></Box>
          <VStack align="stretch" spacing={4}>
            {issues.map((issue, index) => (
              <Box key={index}>
                <IssueForm
                  issue={issue} index={index} role={role} text={text} lang={lang}
                  onChange={(next) => setIssues(issues.map((x, i) => (i === index ? next : x)))}
                  onRemove={issues.length > 1 ? () => setIssues(issues.filter((_, i) => i !== index)) : null}
                />
                {problems[index] && <Text mt={1} fontSize="sm" color="red.600">{problems[index]}</Text>}
              </Box>
            ))}
            {issues.length < 10 && (
              <Button size="sm" variant="outline" leftIcon={<FiPlus />} alignSelf="flex-start"
                onClick={() => setIssues([...issues, emptyIssue(typesForRole(role)[0])])}>
                {zh ? '添加问题' : 'Add problem'}
              </Button>
            )}
          </VStack>
          <Divider my={4} />
          <Alert status="info" variant="subtle" rounded="md" fontSize="sm">
            <AlertIcon />
            {zh ? '保存后这条记录即为“已纠错”。系统记录是谁、何时修改；之后可在“纠错记录”里查看、修改或删除。'
              : 'Saving marks the record as corrected. Who and when is logged; edit or delete later under Corrections.'}
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>{zh ? '取消' : 'Cancel'}</Button>
          <Button colorScheme="blue" onClick={save} isLoading={saving} isDisabled={problems.some(Boolean)}>{zh ? '保存' : 'Save'}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
